import { BellAlertIcon, EnvelopeIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { itemCountPerPage } from 'config'
import { useEffect, useState } from 'react'
import {
  createAccountsReceivable,
  deleteAccountsReceivable,
  downloadAccountsReceivableExcel,
  downloadAccountsReceivablePdf,
  getAccountsReceivables,
  getAccountsReceivableSettings,
  remindAccountsReceivable,
  saveAccountsReceivableSettings,
  sendAccountsReceivablePaidEmail,
  updateAccountsReceivable,
} from 'services'
import { Button, Input2, Pagination } from 'stories/components'
import { confirm, renderHeader } from 'utils'

import { AccountsReceivableModal } from './AccountsReceivableModal'
import { AccountsReceivableSettingsModal } from './AccountsReceivableSettingsModal'
import type { IAccountsReceivable, IAccountsReceivableSettings } from './types'

const money = (value: number) =>
  `$${(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const showDate = (value?: string | null) => {
  if (!value) return ''
  const [year, month, day] = String(value).slice(0, 10).split('-')
  if (!year || !month || !day) return ''
  return `${month}/${day}/${year}`
}

export const AccountsReceivable = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<IAccountsReceivable[]>([])
  const [settings, setSettings] = useState<IAccountsReceivableSettings | null>(null)
  const [query, setQuery] = useState('')
  const [ready, setReady] = useState(false)
  const [filters, setFilters] = useState({
    orderBy: 'createdAt',
    orderDir: '-1',
    pageNum: 0,
    paid: 'All',
    count: itemCountPerPage,
  })
  const [totalCount, setTotalCount] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [unpaidAmount, setUnpaidAmount] = useState(0)
  const [editing, setEditing] = useState<IAccountsReceivable | null>(null)
  const [open, setOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [startingText, setStartingText] = useState('1')

  useEffect(() => {
    getAccountsReceivableSettings().then((saved) => {
      setSettings(saved)
      setStartingText(String(saved?.startingInvoiceNo || 1))
    })
  }, [])

  useEffect(() => {
    if (!ready) return
    const timeOutId = setTimeout(() => !loading && onChangeFilter('pageNum', 0), 700)
    return () => clearTimeout(timeOutId)
  }, [query])

  useEffect(() => {
    refetch()
  }, [filters])

  const refetch = () => {
    setLoading(true)
    getAccountsReceivables({ ...filters, query, paid: filters.paid === 'All' ? '' : filters.paid })
      .then((res) => {
        setReady(true)
        setData(res.results || [])
        setTotalCount(res.totalCount || 0)
        setTotalAmount(res.totalAmount || 0)
        setUnpaidAmount(res.unpaidAmount || 0)
      })
      .finally(() => setLoading(false))
  }

  const onChangeFilter = (key: string, value: any) => {
    const next: any = cloneDeep(filters)
    next[key] = value
    if (key !== 'pageNum') next.pageNum = 0
    setFilters(next)
  }

  const onSave = async (payload: Record<string, any>) => {
    if (editing) await updateAccountsReceivable(editing.id, payload)
    else await createAccountsReceivable(payload)
    setOpen(false)
    setEditing(null)
    refetch()
  }

  const onDelete = async (item: IAccountsReceivable) => {
    const result = await confirm(`Delete invoice ${item.invoiceNo}?`)
    if (!result) return
    setLoading(true)
    await deleteAccountsReceivable(item.id)
    refetch()
  }

  const onRemind = async (item: IAccountsReceivable) => {
    const result = await confirm(`Email an unpaid reminder for ${item.invoiceNo}? The invoice PDF is attached.`)
    if (!result) return
    setLoading(true)
    try {
      await remindAccountsReceivable(item.id)
    } finally {
      setLoading(false)
    }
    refetch()
  }

  const onPaidEmail = async (item: IAccountsReceivable) => {
    const result = await confirm(`Email the paid notice for ${item.invoiceNo}?`)
    if (!result) return
    setLoading(true)
    try {
      await sendAccountsReceivablePaidEmail(item.id)
    } finally {
      setLoading(false)
    }
  }

  const onSaveSettings = async (payload: Record<string, any>) => {
    const saved = await saveAccountsReceivableSettings(payload)
    setSettings(saved)
    setStartingText(String(saved?.startingInvoiceNo || 1))
    setSettingsOpen(false)
  }

  const onSaveStarting = async () => {
    if (!settings) return
    const startingInvoiceNo = Math.max(1, Math.floor(Number(startingText) || 1))
    if (startingInvoiceNo === Number(settings.startingInvoiceNo || 1)) {
      setStartingText(String(startingInvoiceNo))
      return
    }
    setLoading(true)
    try {
      const saved = await saveAccountsReceivableSettings({ ...settings, startingInvoiceNo })
      setSettings(saved)
      setStartingText(String(saved?.startingInvoiceNo || startingInvoiceNo))
    } finally {
      setLoading(false)
    }
  }

  const headers = [
    { title: 'Invoice #', key: 'invoiceNo' },
    { title: 'Date', key: 'invoiceDate' },
    { title: 'Bill To', key: 'billToName' },
    { title: 'Amount', key: 'amount' },
    { title: 'Due', key: 'dueDate' },
  ]

  return (
    <div className="invoices-container">
      <div className="relative bg-white">
        <h2 className="text-2xl font-bold mb-2">Accounts Receivables</h2>
        <p className="text-sm text-gray-500 mb-4">Invoices for money owed to the company. Generate a PDF or Excel copy.</p>
        <LayoutLoading show={loading} />
        <div className="flex gap-4 my-2 justify-between items-center">
          <div className="flex gap-3 items-center">
            <div className="w-72">
              <Input2
                type="search"
                title="Search"
                hasIcon
                icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
                value={query}
                onChange={(value) => setQuery(value)}
              />
            </div>
            <select
              className="border border-gray-300 rounded px-2 py-2 text-sm"
              value={filters.paid}
              onChange={(e) => onChangeFilter('paid', e.target.value)}
            >
              <option value="All">All</option>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="flex gap-2 items-center">
            <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
              Starting invoice #
              <input
                type="number"
                min={1}
                className="border border-gray-300 rounded px-2 py-2 text-sm w-24 text-gray-900"
                value={startingText}
                title="The first invoice of the day uses this number. Later ones count up from there."
                onChange={(e) => setStartingText(e.target.value)}
                onBlur={onSaveStarting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                }}
              />
            </label>
            <Button outline onClick={() => setSettingsOpen(true)}>
              Reminders
            </Button>
            <Button
              onClick={() => {
                setEditing(null)
                setOpen(true)
              }}
            >
              Add
            </Button>
          </div>
        </div>
        <div className="parties-container overflow-auto mb-6 mt-6 shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-900">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3">No</th>
                {headers.map(({ title, key }, index) =>
                  renderHeader({
                    title,
                    sortable: true,
                    key,
                    sortOrder: filters.orderBy === key ? parseInt(filters.orderDir) : 0,
                    index,
                    onSort: (sortKey: string, dir: number) => {
                      const next: any = cloneDeep(filters)
                      next.orderBy = sortKey
                      next.orderDir = String(dir)
                      next.pageNum = 0
                      setFilters(next)
                    },
                  }),
                )}
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.id} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                  <td className="px-4 py-3">{index + 1 + filters.count * filters.pageNum}</td>
                  <td className="px-2 py-3">{item.invoiceNo}</td>
                  <td className="px-2 py-3">{showDate(item.invoiceDate)}</td>
                  <td className="px-2 py-3">{item.billToName}</td>
                  <td className="px-2 py-3">{money(item.amount)}</td>
                  <td className="px-2 py-3">
                    <div>{item.dueLabel}</div>
                    <div className="text-xs text-gray-500">{showDate(item.dueDate)}</div>
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        item.isPaid ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {item.isPaid ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex gap-1">
                      <span
                        className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                        title="Edit"
                        onClick={() => {
                          setEditing(item)
                          setOpen(true)
                        }}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                      <span
                        className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded text-xs"
                        title="Download PDF"
                        onClick={() => downloadAccountsReceivablePdf(item.id, item.invoiceNo)}
                      >
                        PDF
                      </span>
                      <span
                        className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded text-xs"
                        title="Download Excel"
                        onClick={() => downloadAccountsReceivableExcel(item.id, item.invoiceNo)}
                      >
                        Excel
                      </span>
                      {!item.isPaid && (
                        <span
                          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                          title="Email unpaid reminder"
                          onClick={() => onRemind(item)}
                        >
                          <BellAlertIcon className="w-4 h-4" />
                        </span>
                      )}
                      {item.isPaid && (
                        <span
                          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                          title="Email paid notice"
                          onClick={() => onPaidEmail(item)}
                        >
                          <EnvelopeIcon className="w-4 h-4" />
                        </span>
                      )}
                      <span
                        className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                        title="Delete"
                        onClick={() => onDelete(item)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {!data.length && (
                <tr>
                  <td className="px-4 py-6 text-gray-500" colSpan={8}>
                    No invoices yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center text-sm">
          <div>
            Total {money(totalAmount)} · Unpaid {money(unpaidAmount)}
          </div>
          <Pagination
            totalCount={totalCount}
            itemCountPerPage={itemCountPerPage}
            onNavigate={(pageNum: number) => onChangeFilter('pageNum', pageNum)}
            pageNum={filters.pageNum}
          />
        </div>
      </div>
      {open && (
        <AccountsReceivableModal
          item={editing}
          settings={settings}
          onClose={() => {
            setOpen(false)
            setEditing(null)
          }}
          onSave={onSave}
        />
      )}
      {settingsOpen && settings && (
        <AccountsReceivableSettingsModal
          settings={settings}
          onClose={() => setSettingsOpen(false)}
          onSave={onSaveSettings}
        />
      )}
    </div>
  )
}
